/** Placeholder sent as wire text when user sends files without a message. */
export const FILE_ONLY_PLACEHOLDER = "**Please review this document**";

export type ConnectionState =
  | "connecting"
  | "connected"
  | "reconnecting"
  | "disconnected";

// === Client → Server ===

export interface TextContent {
  type: "text";
  text: string;
}

export interface ImageContent {
  type: "image_url";
  image_url: { url: string };
}

/** Content block for outgoing chat messages. */
export type ContentBlock = TextContent | ImageContent;

/** Legacy alias — kept for compatibility. */
export type ChatMessageContent = TextContent;

export interface DocumentAttachment {
  source?: string;
  document_url: string;
  document_type?:
    | "pdf"
    | "docx"
    | "doc"
    | "xlsx"
    | "xls"
    | "csv"
    | "txt"
    | "ppt"
    | "pptx";
  file_name?: string;
  /**
   * Frontend-only flag. When true, this attachment was silently injected
   * (e.g. a JD from pendingJdDocRef) and must NOT be rendered as a visible
   * file chip. This field is stripped before the wire payload is sent to
   * the backend — the backend never sees it.
   */
  silent?: boolean;
}

/** Alias for DocumentAttachment used in chat message wire format. */
export type DocumentContent = DocumentAttachment;

export interface OutgoingMessage {
  user_id: string;
  thread_id: string;
  message: ContentBlock[];
  documents?: DocumentContent[] | null;
  timestamp?: string;
  project_id?: string;
  client_id?: string;
  location?: string;
}

// === Server → Client (discriminated union) ===

export interface TokenCounts {
  input_tokens: number;
  output_tokens: number;
  cost: number;
  currency: "USD";
}

export interface CandidatesListWithScores {
  candidates_id_list: string[];
  scores: { overall_score: number }[];
}

export interface Artifact {
  total_token_count: TokenCounts;
  candidates_list_with_scores?: CandidatesListWithScores;
  job_description_s3_key?: string;
  client_id?: string;
  client_name?: string;
  project_id?: string;
  is_role_title_aligned?: boolean;
}

export interface AuthenticatedEvent {
  type: "authenticated";
}

export interface PingEvent {
  type: "ping";
}

export interface StreamEvent {
  type: "stream";
  user_id: string;
  thread_id: string;
  messages: string | null;
  turn_id?: string;
}

export interface ToolCallingEvent {
  type: "tool_calling";
  tool_name: string;
}

export interface TurnCompleteEvent {
  type: "turn_complete";
  user_id: string;
  thread_id: string;
  thread_name: string | null;
  messages: string;
  artifact: Artifact;
  /** ISO 8601 — absent on guardrail blocks */
  timestamp?: string;
  /** Can be `""` (empty string) or a `string[]` — always check type before use */
  cv_s3_keys?: string | string[];
  turn_id?: string;
}

export interface StoppedEvent {
  type: "stopped";
}

export interface PausedEvent {
  type: "paused";
}

export interface ResumedEvent {
  type: "resumed";
}

export interface TimeoutEvent {
  type: "timeout";
}

export interface ErrorEvent {
  type: "error";
  code: number;
  message: string;
  details: string;
}

export type ServerMessage =
  | AuthenticatedEvent
  | PingEvent
  | StreamEvent
  | ToolCallingEvent
  | TurnCompleteEvent
  | StoppedEvent
  | PausedEvent
  | ResumedEvent
  | TimeoutEvent
  | ErrorEvent;

// === Conversation History (from Backend API) ===

export interface ConversationHistoryItem {
  thread_id: string;
  thread_name?: string;
  conversation_summary?: string;
  message_count: number;
  last_message_at: string;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
  /** Project linked to this thread (projects.thread_id), when any */
  project_name?: string | null;
  client_name?: string | null;
}

export interface ConversationHistoryList {
  items: ConversationHistoryItem[];
  total: number;
  page: number;
  limit: number;
}

export interface ConversationMessage {
  role: string;
  content: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
  cv_s3_keys?: string[];
  /** S3 key of a job description created/referenced by the AI agent. */
  job_description_s3_key?: string;
  candidates_list_with_scores?: CandidatesListWithScores;
}

export interface ConversationDetail {
  user_id: string;
  thread_id: string;
  thread_name?: string;
  messages: ConversationMessage[];
  conversation_summary?: string;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ConversationCounters {
  active: number;
  archived: number;
}

// === UI Message Model ===

export type MessageStatus = "sending" | "streaming" | "complete" | "error";

export interface UIMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  status: MessageStatus;
  documents?: DocumentAttachment[];
  errorDetails?: string;
  jdPath?: string;
  /** ISO time the message was sent/received; shown under the bubble. */
  timestamp?: string;
}

// === Pending File Upload Model ===

export interface PendingFile {
  id: string;
  file: File;
  s3Key?: string;
  isUploading: boolean;
  error?: string;
}
